/* eslint-disable max-lines */
import styled from 'styled-components/macro';
import { fontSizePalette, fontWeightPalette, sizePalette, zIndexPalette } from 'utils/styleConstants';
import angleUp from 'images/icons-lucy4/input-icons/angle-up-inputs.svg';
import angleDown from 'images/icons-lucy4/input-icons/angle-down-inputs.svg';
import iconClear from 'images/icons-lucy4/input-icons/times-inputs.svg';

export const SelectWrapper = styled.div<{ display?: string; width?: string }>`
  position: relative;
  ${({ display }) => display && `display:${display}`};
  width: ${({ width }) => (width ? width : '100%')};
`;

export const FeedbackText = styled.p<{ feedbackColor: string; maxWidth?: string }>`
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSizePalette.xxSmall};
  color: ${({ feedbackColor }) => feedbackColor};
  margin: 3px 5px 0 3px;
  text-align: right;
  position: relative;
  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth}`};
`;

export interface DropdownStyles {
  container?: React.CSSProperties;
  control?: React.CSSProperties;
  valueContainer?: React.CSSProperties;
  singleValue?: React.CSSProperties;
  placeholder?: React.CSSProperties;
  input?: React.CSSProperties;
  indicatorsContainer?: React.CSSProperties;
  dropdownIndicator?: React.CSSProperties;
  clearIndicator?: React.CSSProperties;
  menu?: React.CSSProperties;
  menuList?: React.CSSProperties;
  option?: React.CSSProperties;
  noOptionsMessage?: React.CSSProperties;
}

// Small dropdown styles - Updated border colors with focus states
export const createSmallStyles = (
  isDarkMode: boolean,
  appTheme: any,
  customHeight?: string,
  fontSize?: string,
  noBorder?: boolean,
  feedbackColor?: string,
  hasFeedback?: boolean,
  backgroundColor?: string,
  disabled?: boolean,
  minWidthLabel?: string,
  singleValueMargin?: string,
  overflowWidth?: string,
  menuWidth?: string,
): any => ({
  indicatorsContainer: (provided: any) => ({
    ...provided,
    color: isDarkMode ? appTheme.colorPalette.grey.grey7 : undefined,
    '&:hover': {
      color: isDarkMode ? appTheme.colorPalette.grey.grey7 : provided.color,
    },
  }),
  container: (provided: any) => ({
    ...provided,
    width: provided.width,
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    fontSize: fontSize || fontSizePalette.xxSmall,
    fontWeight: fontWeightPalette.light,
    minWidth: minWidthLabel,
    minHeight: customHeight || 20,
    height: customHeight || 20,
    backgroundColor: disabled
      ? isDarkMode
        ? appTheme.colorPalette.grey.grey1
        : appTheme.colorPalette.grey.grey1
      : backgroundColor
      ? backgroundColor
      : isDarkMode
      ? appTheme.colorPalette.grey.grey4
      : appTheme.colorPalette.white,
    // Updated border colors with focus states
    border: hasFeedback
      ? `1px solid ${feedbackColor}`
      : noBorder
      ? 'none'
      : state.menuIsOpen
      ? `1px solid ${appTheme.colorPalette.turquoise.dark}`
      : `1px solid ${isDarkMode ? 'transparent' : 'transparent'}`,
    opacity: disabled ? 0.7 : 1,
    cursor: 'text',
    boxShadow: 'none',
    '&:hover': {
      border: '1px solid transparent',
    },
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    color: disabled
      ? isDarkMode
        ? appTheme.colorPalette.grey.grey5
        : appTheme.colorPalette.grey.grey7
      : isDarkMode
      ? appTheme.colorPalette.grey.grey10
      : appTheme.colorPalette.grey.grey9,
    opacity: disabled ? 0.7 : 1,
    position: 'relative',
    minWidth: 0,
    overflow: 'hidden',
    fontSize: fontSizePalette.xxSmall,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  clearIndicator: (provided: any) => ({
    ...provided,
    padding: sizePalette.spacing[1],
    marginRight: sizePalette.spacing[1],
    color: isDarkMode ? appTheme.colorPalette.grey.grey10 : appTheme.colorPalette.grey.grey6,
    width: '12px',
    height: '12px',
    borderRadius: sizePalette.borderRadius.sm,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    cursor: 'pointer',
    transition: `color ${sizePalette.transitionDuration[200]}, transform ${sizePalette.transitionDuration[100]}`,

    '& svg': {
      display: 'none',
    },

    '&:hover': {
      color: appTheme.statusColors.error,
    },

    position: 'relative',
    backgroundImage: `url(${iconClear})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
  }),
  dropdownIndicator: (
    provided: any,
    state: {
      selectProps: {
        menuIsOpen?: boolean;
      };
    },
  ) => ({
    ...provided,
    color: isDarkMode ? appTheme.colorPalette.grey.grey7 : appTheme.colorPalette.grey.grey8,
    padding: 0,
    '& svg': {
      display: 'none',
    },
    position: 'relative',
    width: '12px',
    height: '12px',
    marginRight: '8px',

    backgroundImage: state.selectProps.menuIsOpen ? `url(${angleUp})` : `url(${angleDown})`,

    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    cursor: 'pointer',
  }),
  menuList: (provided: any) => ({
    ...provided,
    backgroundColor: isDarkMode ? appTheme.colorPalette.grey.grey3 : appTheme.colorPalette.white,
    borderRadius: sizePalette.borderRadius.DEFAULT,
    maxHeight: '256px',
    overflowY: 'auto',
  }),
  menu: (provided: any) => ({
    ...provided,
    backgroundColor: isDarkMode ? appTheme.colorPalette.grey.grey3 : appTheme.colorPalette.white,
    zIndex: zIndexPalette.high,
    width: menuWidth,
    borderRadius: sizePalette.borderRadius.DEFAULT,
    border: `${sizePalette.borderWidth.DEFAULT} solid ${
      isDarkMode ? appTheme.colorPalette.turquoise.accent : appTheme.colorPalette.turquoise.dark
    }`,
    boxShadow: sizePalette.boxShadow.md,
    marginTop: sizePalette.spacing.pxToRem(10),
    overflow: 'hidden',
  }),
  noOptionsMessage: (provided: any) => ({
    ...provided,
    fontSize: fontSizePalette.xxSmall,
    color: isDarkMode ? appTheme.colorPalette.grey.grey8 : appTheme.colorPalette.grey.grey6,
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isFocused
      ? appTheme.colorPalette.withOpacity(
          isDarkMode ? appTheme.colorPalette.grey.grey9 : appTheme.colorPalette.turquoise.dark,
          isDarkMode ? 0.15 : 0.3,
        )
      : state.isSelected
      ? appTheme.colorPalette.withOpacity(
          isDarkMode ? appTheme.colorPalette.grey.grey9 : appTheme.colorPalette.turquoise.dark,
          isDarkMode ? 0.3 : 0.15,
        )
      : 'transparent',
    color: isDarkMode ? appTheme.colorPalette.grey.grey10 : appTheme.colorPalette.grey.grey9,
    fontSize: '12px',
    fontWeight: fontWeightPalette.regular,
    justifyContent: 'flex-start',
    display: 'flex',
    alignContent: 'center',
    border: 0,
    padding: '6px 6px',
    cursor: 'pointer',
    transition: `background-color ${sizePalette.transitionDuration[200]}`,
    '&:hover': {
      backgroundColor: appTheme.colorPalette.withOpacity(
        isDarkMode ? appTheme.colorPalette.grey.grey9 : appTheme.colorPalette.turquoise.dark,
        isDarkMode ? 0.15 : 0.3,
      ),
    },
  }),

  singleValue: (provided: any) => ({
    ...provided,
    fontWeight: fontWeightPalette.regular,
    color: disabled
      ? isDarkMode
        ? appTheme.colorPalette.grey.grey5
        : appTheme.colorPalette.grey.grey7
      : isDarkMode
      ? appTheme.colorPalette.grey.grey10
      : appTheme.colorPalette.grey.grey9,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',

    left: sizePalette.spacing[2],
    right: sizePalette.spacing[2],

    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',

    '& > *': {
      minWidth: 0,
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  }),

  input: (provided: any) => ({
    ...provided,
    minWidth: 1,
    padding: 0,
    margin: 0,
    color: isDarkMode ? appTheme.colorPalette.grey.grey10 : appTheme.colorPalette.grey.grey9,
  }),
  placeholder: (provided: any) => ({
    ...provided,
    fontSize: fontSize || fontSizePalette.xxSmall,
    color: isDarkMode ? appTheme.colorPalette.grey.grey7 : appTheme.colorPalette.grey.grey7,
    fontWeight: fontWeightPalette.light,
  }),
});

// Normal dropdown styles - Updated border colors with focus states
export const createNormalStyles = (
  isDarkMode: boolean,
  appTheme: any,
  customHeight?: string,
  fontSize?: string,
  feedbackColor?: string,
  hasFeedback?: boolean,
  backgroundColor?: string,
  disabled?: boolean,
  minWidthLabel?: string,
  valueContainerPadding?: string,
  singleValueMargin?: string,
  overflowWidth?: string,
  menuWidth?: string,
  width?: string,
  margin?: string,
): any => ({
  indicatorsContainer: (provided: any) => ({
    ...provided,
    height: customHeight || '30px',
    color: isDarkMode ? appTheme.colorPalette.grey.grey7 : undefined,
    '&:hover': {
      color: isDarkMode ? appTheme.colorPalette.grey.grey7 : provided.color,
    },
  }),
  container: (provided: any) => ({
    ...provided,
    width: `${width}` || provided.width,
    margin: `${margin}` || provided.margin,
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    fontSize: fontSize || fontSizePalette.small,
    fontWeight: fontWeightPalette.light,
    minWidth: minWidthLabel,
    minHeight: '28px',
    height: customHeight || '30px',
    backgroundColor: disabled
      ? isDarkMode
        ? appTheme.colorPalette.grey.grey1
        : appTheme.colorPalette.grey.grey1
      : backgroundColor
      ? backgroundColor
      : isDarkMode
      ? appTheme.colorPalette.grey.grey4
      : appTheme.colorPalette.white,
    // Updated border colors with focus states
    border:
      hasFeedback && feedbackColor
        ? `1px solid ${feedbackColor}`
        : state.menuIsOpen
        ? `1px solid ${isDarkMode ? appTheme.colorPalette.turquoise.accent : appTheme.colorPalette.turquoise.dark}`
        : `1px solid ${isDarkMode ? 'transparent' : appTheme.colorPalette.grey.grey5}`,
    opacity: disabled ? 0.7 : 1,
    cursor: 'text',
    boxShadow: 'none',
    '&:hover': {
      border:
        hasFeedback && feedbackColor
          ? `1px solid ${feedbackColor}`
          : `1px solid ${isDarkMode ? appTheme.colorPalette.turquoise.accent : appTheme.colorPalette.turquoise.dark}`,
    },
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    height: customHeight || '30px',
    padding: valueContainerPadding || `0 0 0 ${sizePalette.spacing[2]}`,

    color: disabled
      ? isDarkMode
        ? appTheme.colorPalette.grey.grey5
        : appTheme.colorPalette.grey.grey7
      : isDarkMode
      ? appTheme.colorPalette.grey.grey10
      : appTheme.colorPalette.grey.grey9,
    opacity: disabled ? 0.7 : 1,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  clearIndicator: (provided: any) => ({
    ...provided,
    padding: sizePalette.spacing[1],
    marginRight: sizePalette.spacing[1],
    color: isDarkMode ? appTheme.colorPalette.grey.grey10 : appTheme.colorPalette.grey.grey6,
    width: '20px',
    height: '20px',
    borderRadius: sizePalette.borderRadius.sm,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: fontSizePalette.small,
    cursor: 'pointer',
    transition: `color ${sizePalette.transitionDuration[200]}, transform ${sizePalette.transitionDuration[100]}`,
    '&:hover': {
      color: appTheme.statusColors.error,
    },

    '&:active': {
      transform: 'scale(0.95)',
    },
    '&::before': {
      content: '"✕"',
    },
  }),
  dropdownIndicator: (
    provided: any,
    state: {
      selectProps: {
        menuIsOpen?: boolean;
      };
    },
  ) => ({
    ...provided,
    color: isDarkMode ? appTheme.colorPalette.grey.grey7 : appTheme.colorPalette.grey.grey8,
    padding: `8px ${sizePalette.spacing[2]} 8px 0`,
    '&:hover': {
      color: isDarkMode ? appTheme.colorPalette.grey.grey9 : appTheme.colorPalette.grey.grey6,
    },

    '& svg': {
      display: 'none',
    },

    position: 'relative',
    width: '16px',
    height: '16px',
    marginRight: '8px',

    backgroundImage: state.selectProps.menuIsOpen ? `url(${angleUp})` : `url(${angleDown})`,

    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    cursor: 'pointer',
  }),
  menuList: (provided: any) => ({
    ...provided,
    backgroundColor: isDarkMode ? appTheme.colorPalette.grey.grey3 : appTheme.colorPalette.white,
    padding: '2px',
    borderRadius: sizePalette.borderRadius.DEFAULT,
    maxHeight: '256px',
    overflowY: 'auto',
  }),
  menu: (provided: any) => ({
    ...provided,
    backgroundColor: isDarkMode ? appTheme.colorPalette.grey.grey3 : appTheme.colorPalette.white,
    zIndex: zIndexPalette.high,
    width: menuWidth,
    borderRadius: sizePalette.borderRadius.DEFAULT,
    border: `${sizePalette.borderWidth.DEFAULT} solid ${
      isDarkMode ? appTheme.colorPalette.turquoise.accent : appTheme.colorPalette.turquoise.dark
    }`,
    boxShadow: sizePalette.boxShadow.md,
    marginTop: sizePalette.spacing.pxToRem(10),
    overflow: 'hidden',
  }),
  noOptionsMessage: (provided: any) => ({
    ...provided,
    fontSize: fontSizePalette.small,
    color: isDarkMode ? appTheme.colorPalette.grey.grey8 : appTheme.colorPalette.grey.grey6,
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isFocused
      ? appTheme.colorPalette.withOpacity(
          isDarkMode ? appTheme.colorPalette.grey.grey9 : appTheme.colorPalette.turquoise.dark,
          isDarkMode ? 0.15 : 0.3,
        )
      : state.isSelected
      ? appTheme.colorPalette.withOpacity(
          isDarkMode ? appTheme.colorPalette.grey.grey9 : appTheme.colorPalette.turquoise.dark,
          isDarkMode ? 0.3 : 0.15,
        )
      : 'transparent',
    color: isDarkMode ? appTheme.colorPalette.grey.grey10 : appTheme.colorPalette.grey.grey9,
    fontSize: fontSizePalette.xSmall,
    fontWeight: fontWeightPalette.regular,
    justifyContent: 'flex-start',
    display: 'flex',
    alignContent: 'center',
    border: 0,
    padding: '6px 6px',
    cursor: 'pointer',
    transition: `background-color ${sizePalette.transitionDuration[200]}`,
    '&:hover': {
      backgroundColor: appTheme.colorPalette.withOpacity(
        isDarkMode ? appTheme.colorPalette.grey.grey9 : appTheme.colorPalette.turquoise.dark,
        isDarkMode ? 0.15 : 0.3,
      ),
    },
  }),
  singleValue: (provided: any) => ({
    ...provided,
    margin: singleValueMargin || provided.margin,
    fontWeight: fontWeightPalette.regular,
    color: disabled
      ? isDarkMode
        ? appTheme.colorPalette.grey.grey5
        : appTheme.colorPalette.grey.grey7
      : isDarkMode
      ? appTheme.colorPalette.grey.grey10
      : appTheme.colorPalette.grey.grey9,
  }),
  input: (provided: any) => ({
    ...provided,
    margin: '0px',
    color: isDarkMode ? appTheme.colorPalette.grey.grey10 : appTheme.colorPalette.grey.grey9,
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: isDarkMode ? appTheme.colorPalette.grey.grey7 : appTheme.colorPalette.grey.grey7,
    fontSize: fontSize || fontSizePalette.small,
    fontWeight: fontWeightPalette.light,
  }),
});
